import {useCallback, useEffect, useMemo, useState} from 'react';
import {useParams, useHistory} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import Typography from 'components/Typography';
import {useQuery, QueryFunctionContext} from 'react-query';
import {format, parse, parseISO} from 'date-fns';
import {
  ClientRole,
  createAddress,
  Customer,
  DataItem,
  ListResponse,
  ManualChargeMethod,
  Request,
  SelectOption,
  Transaction,
  UserAddress,
} from 'types';
import api from 'api';
import states from 'constants/states';
import {CustomerService, UpdateCustomerDto} from 'services/customer.service';
import StatusBadge from 'components/StatusBadge/StatusBadge';
import ViewDetails from 'components/ViewDetails/ViewDetails';
import {
  mapMMStatusText,
  mapMMSubStatusColor,
  mapStatusColor,
  mapStatusText,
} from 'constants/mappings';
import {get} from 'lodash';
import toast from 'react-hot-toast';
import {TypeGooglePlaceDetail} from 'components/GooglePlaceAutoComplete';
import CheckIcon from 'pages/Requests/CheckIcon';
import {phoneFormat} from 'helpers/phoneFormat';
import {useFilter} from './useFilter';
import {CompanyProperty} from 'types/property';
import useSort from 'hooks/useSort';
import {CreditCardType} from 'components/CreditCardType';
import {
  CLIENT_ROLES_OPTIONS,
  MANUAL_PAYMENT_TYPE_OPTIONS_LABELS,
} from 'constants/select-options';
import styles from './UserForm.module.scss';
import useCompanyConfig from 'hooks/useCompanyConfig';

interface Option {
  label: string;
  value: string | number;
}
interface FormValue {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  phone_2?: string;
  phone_3?: string;
  birthday: string;
  customer_company: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  zipcode: string;
  state: Option;
  client_role: SelectOption<ClientRole>;
}
interface PaginatedResponse<T> {
  count: number;
  limit: number;
  offset: number;
  results: T[];
}

async function getUserById({queryKey}: QueryFunctionContext) {
  const [, userId] = queryKey;

  if (!userId) return undefined;

  const {data} = await api.get<Customer>(`/customers/${userId}/`);

  return data;
}

async function getUserCards({queryKey}: QueryFunctionContext) {
  const [, userId] = queryKey;

  if (!userId) return undefined;

  const {data} = await api.get<any[]>(`/payment/method/?customer=${userId}`);

  return data;
}

async function getUserTransactions({queryKey}: QueryFunctionContext<any[]>) {
  const [, userId, page, order] = queryKey;

  if (!userId) return undefined;

  const {data} = await api.get<PaginatedResponse<Transaction>>(
    `/payment/transactions/`,
    {
      params: {
        offset: +page * 20,
        limit: 20,
        customer_id: userId,
        ordering: order,
      },
    }
  );

  return data;
}
async function getUserOrders({queryKey}: QueryFunctionContext<any[]>) {
  const [, customer_id, page, filters] = queryKey;
  if (!customer_id) return;

  const params = {
    offset: +page * 20,
    limit: 20,
    source: 'micromerchant',
    zip_code:
      filters.location.length > 0
        ? filters.location.reduce(
            (pr: [], curr: any) => [...pr, curr.label],
            []
          )
        : undefined,
    preference_date: filters.date
      ? format(filters.date, 'yyyy-MM-dd')
      : undefined,

    preference_time: filters.preferenceTime
      ? filters.preferenceTime.value
      : undefined,
    priority: filters.priority ? filters.priority.value : undefined,
    is_paid: filters.paid ? filters.paid.value === 'true' : undefined,
    attempt_start: filters.attemptStart
      ? filters.attemptStart.value
      : undefined,
    attempt_end: filters.attemptEnd ? filters.attemptEnd.value : undefined,
    landlord: filters.landlord ? filters.landlord.value : undefined,
    property_name: filters.propertyName
      ? filters.propertyName.value
      : undefined,
    property_id: filters.propertyAddress
      ? filters.propertyAddress.value
      : undefined,

    start_date: filters.createdDate.start
      ? format(filters.createdDate.start, 'yyyy-MM-dd')
      : undefined,
    end_date: filters.createdDate.end
      ? format(filters.createdDate.end, 'yyyy-MM-dd')
      : undefined,
    customer_id,
    offering_type: filters.offeringType
      ? filters.offeringType.value
      : undefined,
    status: filters.orderStatus ? filters.orderStatus.value : undefined,
  };
  const {data} = await api.get<PaginatedResponse<any>>(`/requests/`, {
    params,
  });

  return data;
}

async function getFilledOrders({queryKey}: QueryFunctionContext<any[]>) {
  const [, userId] = queryKey;

  const {data} = await api.get<ListResponse<Request>>('/requests/', {
    params: {
      customer_id: userId,
      status: 'in_progress',
      source: 'micromerchant',
      limit: 100,
      is_paid: false,
    },
  });

  return data;
}

async function getStates({queryKey}: QueryFunctionContext<string[]>) {
  const [, keyWord] = queryKey;

  const {data} = await api.get<PaginatedResponse<any>>(
    '/address-information/states/',
    {
      params: {
        limit: 40,
        search: keyWord,
      },
    }
  );
  return data;
}

async function getCities({queryKey}: QueryFunctionContext<string[]>) {
  const [, keyWordCity] = queryKey;

  const {data} = await api.get<PaginatedResponse<any>>(
    '/address-information/cities/',
    {
      params: {
        limit: 20,
        search: keyWordCity,
      },
    }
  );
  return data;
}
async function getLandlordList() {
  const {data} = await api.get<ListResponse<CompanyProperty['landlord']>>(
    '/users/',
    {
      params: {role: 'landlord'},
    }
  );

  return data;
}

async function getCompanyProperties({queryKey}: QueryFunctionContext<any[]>) {
  const [, page, isServiceProvider] = queryKey;
  if (isServiceProvider) return;
  const {data} = await api.get<ListResponse<CompanyProperty>>(`/properties/`, {
    params: {
      limit: 20,
      page: +page * 20,
    },
  });

  return data;
}

type FormData = Omit<createAddress, 'label'>;

function useUserForm(user_id?: number) {
  const {userId} = useParams<{userId: string}>();
  const {push, goBack} = useHistory();
  const {
    control: addressControl,
    setValue: setAddressValee,
    handleSubmit: handleAddressSubmit,
    reset: resetAddress,
  } = useForm<FormData>();

  const [submitting, setSubmitting] = useState(false);
  const [keyWord, setKeyWord] = useState('');
  const [keyWordCity, setKeyWordCity] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [status, setStatus] = useState(
    sessionStorage.getItem('user_tab') || ''
  );
  const [addressesForUpdate, setAddressesForUpdate] = useState<UserAddress[]>(
    []
  );
  const [selectCardModal, setSelectCardModal] = useState(false);
  const [accessCardModal, setAccessCardModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [addressModalVisible, setAddressModalVisible] = useState(false);
  const {onSort, order, sortBy, sortOrder} = useSort('transaction_id', 'desc');
  const [cardId, setCardIs] = useState<SelectOption>();

  const [copayItems, setCopayItems] = useState<any[]>([]);
  const [selectedOrders, setSelectedOrders] = useState<any[]>([]);
  const [allSelected, setAllSelected] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [data, setData] = useState<{
    preference_time: null | string;
    preference_date: null | Date;
    priority: null | string;
  }>({
    preference_time: null,
    preference_date: null,
    priority: null,
  });
  const [locationPreview, setLocationPreview] = useState({
    latitude: 40.7596958,
    longitude: -73.9650045,
  });
  const [deliveryAddressId, setDeliveryAddressId] = useState(0);
  const [openAddressModal, setOpenAddressModal] = useState(false);
  const [orderStatus, setOrderStatus] = useState('');

  useEffect(() => {
    setCurrentPage(1);
  }, [status]);
  const {
    isServiceProvider,
    isPropertyManager,
    isMSPProvider,
    isPickupDropoff,
    hasFeature,
  } = useCompanyConfig();
  const {
    handleSubmit,
    register,
    control,
    formState: {errors},
    reset,
    setValue,
  } = useForm<FormValue>();
  const {
    filters,
    filtered,
    dispatchFilter,
    tempFilters,
    setTempFilters,
    applyFilter,
    resetFilter,
  } = useFilter();

  const {data: stateList} = useQuery(['requests', keyWord], getStates);
  const {data: cityList} = useQuery(['requests', keyWordCity], getCities);
  const {
    data: customer,
    refetch,
    isLoading,
  } = useQuery(['user', userId], getUserById);

  const {data: cards, refetch: refetchCards} = useQuery(
    ['card', userId || user_id],
    getUserCards
  );
  const {data: transactions} = useQuery(
    ['transactions', userId || user_id, currentPage - 1, order],
    getUserTransactions
  );
  const {data: orders, refetch: refetchOrders} = useQuery(
    ['user_orders', userId || user_id, currentPage - 1, filters],
    getUserOrders
  );

  const {data: filledOrders} = useQuery(
    ['user_filled_orders', userId || user_id],
    getFilledOrders
  );
  const {data: landlordList} = useQuery(['landlords'], getLandlordList, {
    enabled: isPropertyManager || isMSPProvider,
  });
  const landlordSelectOptions = useMemo(() => {
    if (!landlordList) return [];
    return landlordList?.results?.map((lan) => ({
      label: `${lan?.first_name} ${lan?.last_name}`,
      value: lan?.id!,
    }));
  }, [landlordList]);
  const {data: properties} = useQuery(
    ['properties', currentPage - 1, isServiceProvider],
    getCompanyProperties
  );

  const {propertyNameOptions, propertyAddressOptions} = useMemo(() => {
    const options: {
      propertyNameOptions: SelectOption[];
      propertyAddressOptions: SelectOption[];
    } = {
      propertyNameOptions: [],
      propertyAddressOptions: [],
    };
    if (!properties || !properties?.results?.length) return options;
    properties?.results?.forEach((prop) => {
      const nameOption: SelectOption = {
        label: prop?.name,
        value: prop?.name,
      };
      const addressOption: SelectOption = {
        label: prop?.address?.display_address,
        value: prop?.id,
      };
      options.propertyNameOptions.push(nameOption);
      options.propertyAddressOptions.push(addressOption);
    });
    return options;
  }, [properties]);

  function transformData(
    data: Request[],
    isRequest: boolean,
    source: 'app' | 'micro'
  ) {
    return data.map((item) => ({
      ...item,
      sync_time: get(item, 'sync_date', '') + ' ' + item.sync_time,
      number: (
        <Typography variant="strong">
          #M{item.sequential_order_number}
        </Typography>
      ),
      is_paid: item.is_paid ? 'Yes' : 'No',
      status:
        source !== 'app' ? (
          // @ts-ignore
          <StatusBadge
            /*// @ts-ignore*/
            color={mapMMSubStatusColor[get(item, 'status', '')]}
          >
            {/*// @ts-ignore*/}
            {mapMMStatusText[get(item, 'status', '')]}
          </StatusBadge>
        ) : (
          /*// @ts-ignore*/
          <StatusBadge color={mapStatusColor[get(item, 'status', '')]}>
            {/*// @ts-ignore*/}
            {mapStatusText[get(item, 'status', '')]}
          </StatusBadge>
        ),
      view: <ViewDetails to={`/requests/${item.id}`} />,
      payment_method: item.payment_method
        ? MANUAL_PAYMENT_TYPE_OPTIONS_LABELS[
            item.payment_method as ManualChargeMethod
          ]
        : '',
      priority: item.priority
        ? item.priority?.[0]?.toUpperCase() + item.priority?.slice(1)
        : '',
      checkbox: (
        <div
          className={styles.checkbox}
          onClick={(val) => {
            val.stopPropagation();
            const include = isRequest
              ? selectedOrders.find((i) => i === item.id)
              : copayItems.find((i) => i.id === item.id);

            if (include) {
              if (isRequest) {
                setSelectedOrders(selectedOrders.filter((i) => i !== item.id));
              } else {
                setCopayItems(copayItems.filter((i) => i.id !== item.id));
              }
            } else {
              if (isRequest) {
                setSelectedOrders((pr) => [...pr, item.id]);
              } else {
                setCopayItems([
                  ...copayItems,
                  {id: item.id, amount_due: item.amount_due},
                ]);
              }
            }
          }}
        >
          <CheckIcon
            emptySelect={
              isRequest
                ? !selectedOrders.find((i) => i === item.id)
                : !copayItems.find((i) => i.id === item.id)
            }
            allSelected={
              isRequest
                ? selectedOrders.find((i) => i === item.id)
                : copayItems.find((i) => i.id === item.id)
            }
          />
        </div>
      ),
    }));
  }

  useEffect(() => {
    if (!!cards?.length) {
      setCardIs({
        value: cards[0].custom_uuid,
        label: `**** **** **** ${get(cards[0], 'last4', '')} - ${
          cards[0].cardholder_name
        }`,
      });
    }
  }, [cards]);

  useEffect(() => {
    if (customer?.user) {
      reset({
        ...customer.user,
        birthday: customer.birthday
          ? format(
              parse(customer.birthday, 'yyyy-MM-dd', new Date()),
              'MM/dd/yyyy'
            )
          : '',
        phone: customer.user.phone
          ? '(' +
            customer.user.phone.substring(0, 3) +
            ') ' +
            customer.user.phone.substring(3, 6) +
            '-' +
            customer.user.phone.substring(6, 10)
          : '',
        phone_2: customer?.phone_2 ? phoneFormat(customer.phone_2) : undefined,
        phone_3: customer?.phone_3 ? phoneFormat(customer.phone_3) : undefined,
        customer_company: get(customer, 'customer_company', ''),
        state: states.find((state) => state.value === customer.user.state),
        client_role: hasFeature('hasClientRoleFilterOnCustomers')
          ? CLIENT_ROLES_OPTIONS.find(
              (role) =>
                role.value === get(customer, 'user.customer.client_role')
            )
          : undefined,
      });
    }
  }, [customer, reset, hasFeature]);

  async function createNewAddress(newAddress: createAddress) {
    try {
      const {data} = await api.post<any>(`/customers/address/`, {
        customer_id: Number(userId),
        ...newAddress,
      });
      await refetch();
      return data;
    } catch (error) {
      const message = get(
        error,
        'response.data.detail',
        'Something went wrong'
      );
      toast.error(message);
    }
  }

  async function attachNewCardToUser(newCard: any) {
    const data = await api.post<any>(`/payment/card/`, newCard);
    refetchCards();
    return data;
  }

  async function deleteAddress(addressId: number | undefined) {
    const {data} = await api.delete<Customer>(
      `/customers/address/${addressId}/`
    );

    refetch();

    return data;
  }

  async function deleteCard(cardId: number | undefined) {
    const {data} = await api.delete<Customer>(`/payment/method/${cardId}/`);
    refetchCards();
    return data;
  }
  async function deleteCustomer(id: number) {
    try {
      await api.delete(`/customers/${id}/`);
      goBack();
      setDeleteModal(false);
    } catch (err) {}
  }

  async function submit(data: FormValue) {
    setSubmitting(true);

    const customerService = new CustomerService();

    if (customer) {
      const dto: UpdateCustomerDto = {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        birthday: format(
          parse(data.birthday, 'MM/dd/yyyy', new Date()),
          'yyyy-MM-dd'
        ),
        addresses: addressesForUpdate,
        phone: data.phone.replace(/\D/g, ''),
        client_role: data?.client_role?.value,
      };
      if (data?.phone_2 && data?.phone_2.trim() !== '') {
        dto.phone_2 = data?.phone_2?.replace(/\D/g, '');
      }
      if (data?.phone_3 && data?.phone_3.trim() !== '') {
        dto.phone_3 = data?.phone_3?.replace(/\D/g, '');
      }
      if (data?.customer_company && data?.customer_company.trim() !== '') {
        dto.customer_company = data?.customer_company;
      }
      await customerService.update(customer.id, customer.user.id, dto);
      toast.success('Updated successfully');
    } else {
      const user = {
        ...data,
        phone: data.phone.replace(/\D/g, ''),

        birthday: format(
          parse(data.birthday, 'MM/dd/yyyy', new Date()),
          'yyyy-MM-dd'
        ),
      };
      if (data?.phone_2 && data?.phone_2.trim() !== '') {
        user.phone_2 = data?.phone_2?.replace(/\D/g, '');
      }
      if (data?.phone_3 && data?.phone_3.trim() !== '') {
        user.phone_3 = data?.phone_3?.replace(/\D/g, '');
      }
      try {
        await api.post('/customers/', user);
      } catch (error) {
        setSubmitting(false);
      }
    }

    setSubmitting(false);
    if (!userId) {
      push('/people/customers');
    }
  }

  const handleNavigateRequest = useCallback(
    (row: DataItem) => {
      push(`/orders/${row.sequential_order_number}`);
    },
    [push]
  );

  const handleNavigateTransaction = useCallback(
    (row: DataItem) => {
      if (get(row, 'service_name', '') === 'CreditSale') {
        push(`/transactions/${row.id}`);
      }
    },
    [push]
  );

  const onChangeStatus = (val: any) => {
    setOrderStatus(val?.value);
  };

  function onChangeTime(value: any) {
    setData(value);
  }

  const onSelectPlace = (placeDetail: TypeGooglePlaceDetail) => {
    setAddressValee('latitude', placeDetail.lat);
    setAddressValee('longitude', placeDetail.lon);
    setAddressValee('zipcode', placeDetail.zipcode);
    setAddressValee('address_line_1', placeDetail.addressLine1);
    setAddressValee('city', placeDetail.city);
    setAddressValee('state', placeDetail.state);
    setTimeout(() => {
      setLocationPreview({
        latitude: placeDetail.lat,
        longitude: placeDetail.lon,
      });
    }, 300);
  };

  const handleAddressCreation = async (datas: createAddress) => {
    const res = await createNewAddress({
      ...datas,
      customer_id: customer?.id,
    });

    if (get(res, 'status') === 200) {
      refetch();
      setOpenAddressModal(false);
      setDeliveryAddressId(res.data.id);
      resetAddress();
      setLocationPreview({
        latitude: 40.7596958,
        longitude: -73.9650045,
      });
    }
  };

  const submitOrderStatutes = async () => {
    try {
      await api.post('/requests/requests/bulk-update/', {
        request_ids: selectedOrders,
        status: orderStatus,
        preference_date: data.preference_date
          ? format(data.preference_date, 'yyyy-MM-dd')
          : undefined,
        preference_time: data.preference_time || undefined,
        delivery_address_id: deliveryAddressId,
      });
      refetchOrders();
      setShowStatusModal(false);
      setOrderStatus('');
      setSelectedOrders([]);
      setData({
        preference_date: null,
        preference_time: null,
        priority: null,
      });

      setDeliveryAddressId(0);
      toast.success('Successfully updated statutes!');
    } catch (error) {
      toast.error(
        JSON.stringify(get(error, 'response.data')) ||
          'Something wrong with creation. Try again later !'
      );
    }
  };

  const returnType = (type: string) => {
    if (type === 'patient_app') {
      return 'Customer App';
    }
    if (type === 'pos') {
      return 'POS';
    }
    if (type === 'dashboard') {
      return 'Dashboard';
    }
    if (type === 'driver_app_cod') {
      return 'Driver App COD';
    }
  };

  return {
    userId,
    isVendor: get(customer, 'client_role') === 'vendors',
    addresses: customer?.addresses || [],
    cards: cards || [],
    orders: orders?.results
      ? transformData(orders?.results, true, 'micro')
      : [],
    filledOrders: filledOrders?.results
      ? transformData(filledOrders?.results, false, 'micro')
      : [],
    submitting,
    errors,
    control,
    register,
    reset,
    setValue,
    isLoading,
    addressesForUpdate,
    setAddressesForUpdate,
    handleSubmit: handleSubmit(submit),
    createNewAddress,
    attachNewCardToUser,
    deleteAddress,
    deleteCard,
    setKeyWord,
    setKeyWordCity,
    stateList,
    cityList,
    refetch,
    transactions:
      transactions?.results.map((item) => ({
        ...item,
        id: item.transaction_id,
        transaction_id: item.sequential_order_number,
        amount: '$' + item.amount,
        transaction_origin: returnType(item.transaction_origin),
        transaction_date: format(
          parseISO(item.transaction_date),
          'MM/dd/yy EEE hh:mm a'
        ),
        list_rx: `${item.list_rx ? item.list_rx.slice(0, 3).join(', ') : ''} ${
          item.list_rx && item.list_rx.length > 3 ? '...' : ''
        }`,
        list_orders: `${get(item, 'list_orders', []).slice(0, 3).join(', ')} ${
          item?.list_orders?.length > 3 ? '...' : ''
        }`,
        card_type: item?.card_type && <CreditCardType type={item.card_type} />,
        transaction_type:
          item?.transaction_type.charAt(0).toUpperCase() +
          item.transaction_type.slice(1),
        payment_method: MANUAL_PAYMENT_TYPE_OPTIONS_LABELS[item.payment_method],
      })) || [],
    setCurrentPage,
    currentPage,
    status,
    setStatus,
    totalPages:
      status === 'transactions'
        ? Math.round((transactions?.count || 0) / 20)
        : Math.round((customer?.requests?.length || 0) / 20),
    copayItems,
    setCopayItems,
    handleNavigateRequest,
    handleNavigateTransaction,
    accessCardModal,
    setAccessCardModal,
    selectCardModal,
    setSelectCardModal,
    addressModalVisible,
    setAddressModalVisible,
    cardId,
    setCardIs,
    selectedOrders,
    setSelectedOrders,
    allSelected,
    setAllSelected,
    showStatusModal,
    setShowStatusModal,
    onChangeStatus,
    onChangeTime,
    data,
    setData,
    deliveryAddressId,
    setDeliveryAddressId,
    openAddressModal,
    setOpenAddressModal,
    addressControl,
    setAddressValee,
    handleAddressSubmit,
    resetAddress,
    onSelectPlace,
    locationPreview,
    handleAddressCreation,
    push,
    orderStatus,
    setOrderStatus,
    submitOrderStatutes,
    deleteModal,
    setDeleteModal,
    handleDeleteCustomer: deleteCustomer,
    filters,
    dispatchFilter,
    tempFilters,
    setTempFilters,
    applyFilter,
    resetFilter,
    landlordSelectOptions: landlordSelectOptions || [],
    propertyAddressOptions: propertyAddressOptions || [],
    propertyNameOptions: propertyNameOptions || [],
    onSort,
    order,
    sortBy,
    sortOrder,
    isServiceProvider,
    isPropertyManager,
    isPickupDropoff,
    filtered,
    timeline: customer?.timeline || [],
  };
}

export default useUserForm;
